import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useNotify } from "ra-core";
import { FileText, Loader2, Trash2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { ATTACHMENTS_BUCKET } from "../providers/commons/attachments";
import { StorageLink } from "../misc/storage";

// LDF: Angebote (mit Versionen), Rechnungen und Konzepte als PDF am Auftrag. Dateien liegen im privaten Bucket.

type DealDocument = {
  id: number;
  kind: string;
  title: string;
  version: string | null;
  doc_number: string | null;
  doc_date: string | null;
  amount_net: number | null;
  status: string | null;
  file_path: string;
  file_name: string;
  file_size: number | null;
};

const KINDS: Record<string, string> = {
  angebot: "Angebote",
  rechnung: "Rechnungen",
  auftragsbestaetigung: "Auftragsbestätigungen",
  konzept: "Konzepte",
  sonstiges: "Sonstiges",
};

const STATUSES: Record<string, string[]> = {
  angebot: ["entwurf", "versendet", "angenommen", "abgelehnt", "ersetzt"],
  rechnung: ["entwurf", "versendet", "bezahlt", "storniert"],
  auftragsbestaetigung: ["entwurf", "versendet"],
  konzept: ["entwurf", "versendet"],
  sonstiges: ["entwurf", "versendet"],
};

const STATUS_LABEL: Record<string, string> = {
  entwurf: "Entwurf",
  versendet: "Versendet",
  angenommen: "Angenommen",
  abgelehnt: "Abgelehnt",
  ersetzt: "Ersetzt",
  bezahlt: "Bezahlt",
  storniert: "Storniert",
};

const euro = (n: number | null) =>
  n == null ? "" : n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const guessKind = (name: string) =>
  /rechnung|invoice/i.test(name) ? "rechnung" : /konzept|moodboard/i.test(name) ? "konzept" : /auftragsbest/i.test(name) ? "auftragsbestaetigung" : "angebot";

const sha256 = async (file: File) => {
  const hash = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
};

export const DealDocuments = ({ dealId }: { dealId: number | string }) => {
  const supabase = getSupabaseClient();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const { data: documents } = useQuery({
    queryKey: ["deal-documents", dealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deal_documents")
        .select("id,kind,title,version,doc_number,doc_date,amount_net,status,file_path,file_name,file_size")
        .eq("deal_id", dealId)
        .order("doc_date", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as DealDocument[];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["deal-documents", dealId] });

  const upload = async (files: FileList | File[]) => {
    const pdfs = [...files].filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (!pdfs.length) {
      notify("Bitte PDF-Dateien verwenden", { type: "warning" });
      return;
    }
    setUploading(true);
    try {
      for (const file of pdfs) {
        const hash = await sha256(file);
        const path = `deals/${dealId}/${hash.slice(0, 10)}-${file.name.replace(/[^A-Za-z0-9._-]+/g, "_")}`;
        const { error: uploadError } = await supabase.storage
          .from(ATTACHMENTS_BUCKET)
          .upload(path, file, { contentType: "application/pdf", upsert: true });
        if (uploadError) throw uploadError;
        const version = file.name.match(/V\s?(\d+\.\d+)/i)?.[1];
        const { error } = await supabase.from("deal_documents").insert({
          deal_id: dealId,
          kind: guessKind(file.name),
          title: file.name.replace(/\.pdf$/i, ""),
          version: version ? `V${version}` : null,
          doc_date: new Date(file.lastModified).toISOString().slice(0, 10),
          status: "entwurf",
          file_path: path,
          file_name: file.name,
          file_size: file.size,
          sha256: hash,
          source: "upload",
          sales_id: identity?.id ?? null,
        });
        if (error && !String(error.message).includes("duplicate")) throw error;
        if (error) notify(`${file.name} ist bereits hinterlegt`, { type: "info" });
      }
      notify("Dokument hochgeladen – bitte Art, Datum und Betrag prüfen", { type: "success" });
      refresh();
    } catch (e) {
      notify(`Upload fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}`, { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const update = async (id: number, values: Partial<DealDocument>) => {
    const { error } = await supabase.from("deal_documents").update(values).eq("id", id);
    if (error) notify(`Fehler: ${error.message}`, { type: "error" });
    refresh();
  };

  const remove = async (doc: DealDocument) => {
    if (!confirm(`„${doc.title}“ wirklich löschen?`)) return;
    await supabase.storage.from(ATTACHMENTS_BUCKET).remove([doc.file_path]);
    await supabase.from("deal_documents").delete().eq("id", doc.id);
    notify("Dokument gelöscht", { type: "success" });
    refresh();
  };

  const groups = Object.keys(KINDS)
    .map((kind) => [kind, (documents ?? []).filter((d) => d.kind === kind)] as const)
    .filter(([, list]) => list.length);

  return (
    <div
      className={`rounded-lg border p-3 ${dragging ? "border-primary bg-primary/5" : "border-dashed border-border"}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        upload(e.dataTransfer.files);
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground tracking-wide">Angebote & Rechnungen (PDF hierher ziehen)</span>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          PDF hochladen
          <input type="file" accept="application/pdf" multiple className="hidden" onChange={(e) => e.target.files && upload(e.target.files)} />
        </label>
      </div>
      {!groups.length && <p className="text-sm text-muted-foreground">Noch keine Dokumente.</p>}
      {groups.map(([kind, list]) => (
        <div key={kind} className="mb-3">
          <h4 className="mb-1 text-sm font-medium">{KINDS[kind]}</h4>
          {list.map((doc) => (
            <div
              key={doc.id}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border py-1.5 text-sm ${doc.status === "ersetzt" ? "text-muted-foreground" : ""}`}
            >
              <FileText className="h-4 w-4 shrink-0" />
              <StorageLink file={{ path: doc.file_path }} className="min-w-0 flex-1 truncate underline hover:no-underline" title={doc.file_name}>
                {doc.title}
              </StorageLink>
              {doc.version && <span className="text-xs">{doc.version}</span>}
              {doc.doc_number && <span className="text-xs">Nr. {doc.doc_number}</span>}
              <input
                type="date"
                defaultValue={doc.doc_date ?? ""}
                onBlur={(e) => e.target.value !== (doc.doc_date ?? "") && update(doc.id, { doc_date: e.target.value || null })}
                className="h-7 rounded border border-input bg-background px-1 text-xs"
              />
              <input
                type="number"
                step="0.01"
                defaultValue={doc.amount_net ?? ""}
                placeholder="netto €"
                title={euro(doc.amount_net)}
                onBlur={(e) => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  if (v !== doc.amount_net) update(doc.id, { amount_net: v });
                }}
                className="h-7 w-24 rounded border border-input bg-background px-1 text-right text-xs"
              />
              <select
                value={doc.kind}
                onChange={(e) => update(doc.id, { kind: e.target.value, status: STATUSES[e.target.value][0] })}
                className="h-7 rounded border border-input bg-background px-1 text-xs"
              >
                {Object.entries(KINDS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l.replace(/e$|en$/, "")}
                  </option>
                ))}
              </select>
              <select
                value={doc.status ?? ""}
                onChange={(e) => update(doc.id, { status: e.target.value || null })}
                className="h-7 rounded border border-input bg-background px-1 text-xs"
              >
                <option value="">Status …</option>
                {STATUSES[doc.kind].map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(doc)} title="Löschen">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
